import { useState, useCallback } from 'react';
import { Edit3, Save, X, ChevronDown, ChevronUp, Check, Sparkles, Loader2, Eye, RotateCcw, Trash2 } from 'lucide-react';
import BlockEditor from '../content/BlockEditor';
import RichContent from '../content/RichContent';
import api from '../../services/api';

/**
 * QuestionEditPanel — Inline editor for questions during quiz review.
 * Works for custom questions (user_questions) and editorial questions (admin).
 *
 * Props:
 *   question: { id, enonce, reponses, explication, bonne_reponse, difficulte }
 *   correctData: { correct, explication } from correctAnswers
 *   onSaved: (updatedQuestion) => void
 *   source: 'custom' | 'official' — determines which API endpoint to use
 */
function mergeBlocks(blocks) {
    if (!blocks || !Array.isArray(blocks)) return blocks;
    const paragraphs = [];
    const others = [];
    for (const b of blocks) {
        if (b.type === 'paragraph' && b.isActive !== false) paragraphs.push(b.content || '');
        else others.push(b);
    }
    if (paragraphs.length <= 1) return blocks;
    return [{ type: 'paragraph', content: paragraphs.join('<br><br>'), isActive: true }, ...others];
}

export default function QuestionEditPanel({ question, correctData, onSaved, onDeleted, source = 'custom', initialEditing = false, onCancel, onDirtyChange }) {
    const [editing, setEditing] = useState(initialEditing);

    const markDirty = () => { onDirtyChange?.(true); };
    const markClean = () => { onDirtyChange?.(false); };
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    const handleDelete = async () => {
        if (!window.confirm('Supprimer cette question ?')) return;
        try {
            await api.post('/custom/question.php', { action: 'delete', question_id: question.id });
            onDeleted?.(question.id);
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.message || 'Erreur' });
        }
    };

    // Editable state — initialized eagerly when initialEditing=true so the first
    // render already has non-null arrays (avoids the "reponses.map" crash).
    const initEnonce = () => {
        if (!initialEditing) return null;
        return Array.isArray(question.enonce) ? question.enonce : JSON.parse(question.enonce || '[]');
    };
    const initReponses = () => {
        if (!initialEditing) return null;
        const raw = Array.isArray(question.reponses) ? question.reponses : JSON.parse(question.reponses || '[]');
        return raw.map(r => ({ ...r, content: [...(r.content || [])] }));
    };
    const initExplication = () => {
        if (!initialEditing) return null;
        return Array.isArray(question.explication) ? question.explication
            : (correctData?.explication || (question.explication ? JSON.parse(question.explication) : []));
    };
    const [enonce, setEnonce] = useState(initEnonce);
    const [reponses, setReponses] = useState(initReponses);
    const [explication, setExplication] = useState(initExplication);
    const [bonneReponse, setBonneReponse] = useState(initialEditing ? (correctData?.correct || question.bonne_reponse || 'a') : null);
    const [difficulte, setDifficulte] = useState(initialEditing ? (question.difficulte || 1) : null);
    const [openSection, setOpenSection] = useState('explication'); // which accordion is open

    // AutoImprove state
    const [improving, setImproving] = useState(false);
    const [improved, setImproved] = useState(null); // { enonce, reponses, explication, bonne_reponse, difficulte }
    const [improveInfo, setImproveInfo] = useState(null); // { model, tokens_used, generation_time }
    const [wasAutoImproved, setWasAutoImproved] = useState(false); // track if AutoImprove was applied before save

    const startEdit = useCallback(() => {
        // Initialize from question data
        const qEnonce = Array.isArray(question.enonce) ? question.enonce : JSON.parse(question.enonce || '[]');
        const qReponses = Array.isArray(question.reponses) ? question.reponses : JSON.parse(question.reponses || '[]');
        const qExplication = correctData?.explication || [];

        setEnonce([...qEnonce]);
        setReponses(qReponses.map(r => ({ ...r, content: [...(r.content || [])] })));
        setExplication([...qExplication]);
        setBonneReponse(correctData?.correct || question.bonne_reponse || 'a');
        setDifficulte(question.difficulte || 1);
        setEditing(true);
        setMsg(null);
    }, [question, correctData]);

    const cancelEdit = () => {
        setEditing(false);
        setMsg(null);
        markClean();
        onCancel?.();
    };

    const handleSave = async () => {
        setSaving(true);
        setMsg(null);
        try {
            let data;
            if (source === 'official') {
                // Editorial questions — use admin endpoint
                const resp = await api.post('/admin/questions.php', {
                    action: 'save_question',
                    question_source: 'official',
                    id: question.id,
                    enonce,
                    reponses,
                    explication,
                    bonne_reponse: bonneReponse,
                    difficulte,
                    status: question.status || 'ONLINE',
                });
                data = resp.data;
            } else {
                // Custom questions — use custom endpoint
                const resp = await api.put('/custom/question.php', {
                    question_id: question.id,
                    enonce,
                    reponses,
                    explication,
                    bonne_reponse: bonneReponse,
                    difficulte,
                });
                data = resp.data;
            }
            if (data.success) {
                // Add AutoImprove review if applicable
                if (wasAutoImproved && source === 'official') {
                    const info = typeof wasAutoImproved === 'object' ? wasAutoImproved : {};
                    try {
                        await api.post('/admin/questions.php', {
                            action: 'add_review',
                            question_source: 'official',
                            question_id: question.id,
                            review: {
                                reviewer: 'AutoImprove',
                                status: 'validated',
                                confidence: 7,
                                comment: `AutoImprove applique (${info.model || 'IA'}, ${info.tokens_used || '?'} tokens)`,
                            },
                        });
                    } catch (e) { console.warn("Erreur:", e); }
                    setWasAutoImproved(false);
                }
                setMsg({ type: 'success', text: 'Question sauvegardée' });
                setEditing(false);
                markClean();
                if (onSaved) onSaved({ enonce, reponses, explication, bonne_reponse: bonneReponse, difficulte });
            } else {
                setMsg({ type: 'error', text: data.message || 'Erreur' });
            }
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.message || 'Erreur réseau' });
        } finally {
            setSaving(false);
        }
    };

    const handleImprove = async () => {
        setImproving(true);
        setImproved(null);
        setImproveInfo(null);
        setMsg(null);
        try {
            // Use current edit state if editing, otherwise original question
            const currentEnonce = editing ? enonce : (Array.isArray(question.enonce) ? question.enonce : JSON.parse(question.enonce || '[]'));
            const currentReponses = editing ? reponses : (Array.isArray(question.reponses) ? question.reponses : JSON.parse(question.reponses || '[]'));
            const currentExplication = editing ? explication : (correctData?.explication || []);
            const currentBonne = editing ? bonneReponse : (correctData?.correct || question.bonne_reponse || 'a');
            const currentDiff = editing ? difficulte : (question.difficulte || 2);

            const { data } = await api.post('/admin/improve-question.php', {
                enonce: currentEnonce,
                reponses: currentReponses,
                bonne_reponse: currentBonne,
                explication: currentExplication,
                difficulte: currentDiff,
            });

            if (data.success) {
                if (data.data.no_change) {
                    setMsg({ type: 'success', text: 'L\'IA estime que cette question est deja excellente.' });
                } else {
                    setImproved(data.data.improved);
                    setImproveInfo({ model: data.data.model, tokens_used: data.data.tokens_used, generation_time: data.data.generation_time });
                }
            }
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.message || 'Erreur IA' });
        } finally {
            setImproving(false);
        }
    };

    const applyImproved = () => {
        if (!improved) return;
        if (!editing) startEdit();
        const infoSnapshot = improveInfo ? { ...improveInfo } : null;
        const apply = () => {
            setEnonce(improved.enonce);
            setReponses(improved.reponses);
            setExplication(improved.explication);
            setBonneReponse(improved.bonne_reponse);
            setDifficulte(improved.difficulte);
            setImproved(null);
            setImproveInfo(null);
            setWasAutoImproved(infoSnapshot || true);
            setMsg({ type: 'success', text: 'Version IA appliquee — sauvegardez pour confirmer.' });
        };
        if (!editing) setTimeout(apply, 50);
        else apply();
    };

    const dismissImproved = () => {
        setImproved(null);
        setImproveInfo(null);
    };

    const updateReponseContent = (idx, newContent) => {
        setReponses(prev => prev.map((r, i) => i === idx ? { ...r, content: newContent } : r));
    };

    if (!editing) {
        return (
            <div className="qep">
                <div className="qep__toolbar">
                    <button className="qep__edit-btn" onClick={startEdit} title="Modifier cette question">
                        <Edit3 size={14} /> Modifier
                    </button>
                    {source === 'official' && (
                        <button className="qep__improve-btn" onClick={handleImprove} disabled={improving} title="Ameliorer avec l'IA">
                            {improving ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
                            {improving ? 'Analyse...' : 'AutoImprove'}
                        </button>
                    )}
                </div>
                {msg && <div className={`qep__msg qep__msg--${msg.type}`}>{msg.text}</div>}
                {improved && (
                    <div className="qep__preview">
                        <div className="qep__preview-header">
                            <Sparkles size={14} /> Version amelioree par IA
                            {improveInfo && <span className="qep__preview-meta">{improveInfo.model} — {improveInfo.tokens_used} tokens — {improveInfo.generation_time}s</span>}
                        </div>
                        <div className="qep__preview-content">
                            <div className="qep__preview-section">
                                <strong>Enonce :</strong>
                                <RichContent blocks={mergeBlocks(improved.enonce)} />
                            </div>
                            <div className="qep__preview-section">
                                <strong>Reponses :</strong>
                                {improved.reponses.map((r, i) => (
                                    <div key={r.id || i} className={`qep__preview-reponse ${improved.bonne_reponse === r.id ? 'qep__preview-reponse--correct' : ''}`}>
                                        <span className="qep__preview-rep-id">{(r.id || '?').toUpperCase()}.</span>
                                        <RichContent blocks={mergeBlocks(r.content || [])} />
                                        {improved.bonne_reponse === r.id && <Check size={12} />}
                                    </div>
                                ))}
                            </div>
                            <div className="qep__preview-section">
                                <strong>Explication :</strong>
                                <RichContent blocks={mergeBlocks(improved.explication)} />
                            </div>
                        </div>
                        <div className="qep__preview-actions">
                            <button className="btn btn-sm btn-primary" onClick={applyImproved}>
                                <Check size={14} /> Appliquer et editer
                            </button>
                            <button className="btn btn-sm btn-ghost" onClick={dismissImproved}>
                                <X size={14} /> Ignorer
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="qep qep--editing">
            <div className="qep__header">
                <span className="qep__title">Modifier la question {question.id ? `(#${question.id})` : ''}</span>
                <div className="qep__actions">
                    {source === 'official' && (
                        <button className="btn btn-sm btn-outline" onClick={handleImprove} disabled={improving || saving}>
                            {improving ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
                            {improving ? 'Analyse...' : 'AutoImprove'}
                        </button>
                    )}
                    <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
                        <Save size={14} /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                    </button>
                    {source === 'custom' && onDeleted && (
                        <button className="btn btn-sm btn-ghost" onClick={handleDelete} title="Supprimer cette question"
                            style={{ color: 'var(--danger)' }}>
                            <Trash2 size={14} />
                        </button>
                    )}
                    <button className="btn btn-sm btn-ghost" onClick={cancelEdit}>
                        <X size={14} />
                    </button>
                </div>
            </div>

            {msg && <div className={`qep__msg qep__msg--${msg.type}`}>{msg.text}</div>}

            {/* Énoncé */}
            <div className="qep__section">
                <button className="qep__section-toggle" onClick={() => setOpenSection(openSection === 'enonce' ? null : 'enonce')}>
                    {openSection === 'enonce' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    Énoncé
                </button>
                {openSection === 'enonce' && (
                    <div className="qep__section-content">
                        <BlockEditor blocks={enonce} onChange={v => { setEnonce(v); markDirty(); }} compact folder="user-questions" />
                    </div>
                )}
            </div>

            {/* Réponses */}
            <div className="qep__section">
                <button className="qep__section-toggle" onClick={() => setOpenSection(openSection === 'reponses' ? null : 'reponses')}>
                    {openSection === 'reponses' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    Réponses
                </button>
                {openSection === 'reponses' && (
                    <div className="qep__section-content">
                        {reponses.map((rep, idx) => (
                            <div key={rep.id || idx} className={`qep__reponse ${bonneReponse === rep.id ? 'qep__reponse--correct' : ''}`}>
                                <div className="qep__reponse-header">
                                    <span className="qep__reponse-label">{(rep.id || String.fromCharCode(97 + idx)).toUpperCase()}.</span>
                                    <button
                                        className={`qep__reponse-correct-btn ${bonneReponse === rep.id ? 'qep__reponse-correct-btn--active' : ''}`}
                                        onClick={() => { setBonneReponse(rep.id); markDirty(); }}
                                        title="Définir comme bonne réponse"
                                    >
                                        <Check size={12} /> {bonneReponse === rep.id ? 'Bonne réponse' : 'Correcte ?'}
                                    </button>
                                </div>
                                <BlockEditor blocks={rep.content || []} onChange={(newContent) => { updateReponseContent(idx, newContent); markDirty(); }} compact folder="user-questions" />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Explication */}
            <div className="qep__section">
                <button className="qep__section-toggle" onClick={() => setOpenSection(openSection === 'explication' ? null : 'explication')}>
                    {openSection === 'explication' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    Explication
                </button>
                {openSection === 'explication' && (
                    <div className="qep__section-content">
                        <BlockEditor blocks={explication} onChange={v => { setExplication(v); markDirty(); }} folder="user-questions" />
                    </div>
                )}
            </div>

            {/* Difficulté */}
            <div className="qep__section qep__section--inline">
                <span className="qep__diff-label">Difficulté :</span>
                {[1, 2, 3].map(d => (
                    <button key={d}
                        className={`qep__diff-btn ${difficulte === d ? 'qep__diff-btn--active' : ''}`}
                        onClick={() => { setDifficulte(d); markDirty(); }}
                    >
                        {'★'.repeat(d)}
                    </button>
                ))}
            </div>

        </div>
    );
}
